import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { BadRequest } from 'fejl';
import { Job, Resolution, Status, WaitingOn, AmendmentVisibility } from '@taxfyle/api-internal/internal/job_pb';
import { Consultation } from '@taxfyle/api-internal/internal/consultation_pb';
import { timestampToISO, decimalToNumber, durationToMinutes } from '../utils/grpcUtil';

/**
 * Maps a given value to a decimal number when it is not null
 */
function maybeDecimalToNumber(value) {
  return value ? decimalToNumber(value) : 0;
}

/**
 * Maps a Job Proto to a Job DTO.
 *
 * @param {Job} payload - The gRPC Job.
 * @returns {JobDTO} The job DTO.
 */
export function mapGrpcJobToJobDTO(payload) {
  var _payload$name, _payload$description, _payload$ownerTeamId, _payload$legend, _payload$legend2, _payload$legend3, _payload$conversation, _payload$requestedPro, _payload$requestedPro2;
  var provider = payload.membersList.find(function (m) {
    return m.type === Job.MemberType.PROVIDER && m.role === Job.MemberRole.CHAMPION;
  });
  return {
    id: payload.id,
    short_id: payload.shortId,
    name: (_payload$name = payload.name) === null || _payload$name === void 0 ? void 0 : _payload$name.value,
    description: (_payload$description = payload.description) === null || _payload$description === void 0 ? void 0 : _payload$description.value,
    provider_user_id: provider === null || provider === void 0 ? void 0 : provider.userId,
    workspace_id: payload.workspaceId,
    coupon_codes: payload.couponCodesList,
    coupon_ids: payload.couponIdsList,
    info_lines: payload.infoLinesList,
    owner_team_id: (_payload$ownerTeamId = payload.ownerTeamId) === null || _payload$ownerTeamId === void 0 ? void 0 : _payload$ownerTeamId.value,
    team_ids: payload.teamIdsList,
    payment_info_status: mapGrpcJobPaymentInfoStatus(payload.paymentInfoStatus),
    legend_name: (_payload$legend = payload.legend) === null || _payload$legend === void 0 ? void 0 : _payload$legend.name,
    legend_id: (_payload$legend2 = payload.legend) === null || _payload$legend2 === void 0 ? void 0 : _payload$legend2.id,
    legend_version: (_payload$legend3 = payload.legend) === null || _payload$legend3 === void 0 ? void 0 : _payload$legend3.version,
    members: payload.membersList.map(function (m) {
      return {
        team_id: m.teamId,
        role: mapGrpcJobMemberRole(m.role),
        type: mapGrpcJobMemberType(m.type),
        user: {
          email: m.email,
          family_name: m.familyName,
          display_name: m.displayName,
          given_name: m.givenName,
          picture: m.picture,
          user_id: m.userId,
          user_public_id: m.id
        }
      };
    }),
    layerConversation: (_payload$conversation = payload.conversationId) === null || _payload$conversation === void 0 ? void 0 : _payload$conversation.value,
    amendments: payload.amendmentsList.map(function (a) {
      return {
        description: a.description,
        internal_note: a.internalNote,
        provider_amount: decimalToNumber(a.providerAmount),
        customer_amount: decimalToNumber(a.customerAmount),
        visibility: mapGrpcJobAmendmentVisibility(a.visibility),
        date_created: timestampToISO(a.dateCreated),
        creator: {
          email: a.creator.email,
          family_name: a.creator.familyName,
          display_name: a.creator.displayName,
          given_name: a.creator.givenName,
          picture: a.creator.picture,
          user_id: a.creator.userId,
          user_public_id: a.creator.id
        }
      };
    }),
    billing_type: mapGrpcJobBillingType(payload.billingType),
    legend: payload.legend ? mapGrpcJobLegend(payload.legend) : undefined,
    answers: payload.answersList.map(function (a) {
      return {
        id: a.id,
        answer: JSON.parse(a.value)
      };
    }),
    coverage_states: payload.coverageStatesList,
    status: mapGrpcJobStatus(payload.status),
    resolution: mapGrpcJobResolution(payload.resolution),
    years: payload.yearsList,
    total: payload.total ? decimalToNumber(payload.total) : undefined,
    milestones: payload.milestonesList.map(function (m) {
      return {
        id: m.id,
        title: m.title,
        description: m.description,
        completed: m.completed,
        tasks: m.tasksList.map(function (t) {
          var _t$description, _t$completedById, _t$completedByPublicI;
          return {
            id: t.id,
            title: t.title,
            description: (_t$description = t.description) === null || _t$description === void 0 ? void 0 : _t$description.value,
            designee: mapGrpcJobTaskDesignee(t.designee),
            completed: t.completed,
            date_completed: timestampToISO(t.dateCompleted),
            completed_by_id: (_t$completedById = t.completedById) === null || _t$completedById === void 0 ? void 0 : _t$completedById.value,
            completed_by_public_id: (_t$completedByPublicI = t.completedByPublicId) === null || _t$completedByPublicI === void 0 ? void 0 : _t$completedByPublicI.value
          };
        })
      };
    }),
    paid_amount: maybeDecimalToNumber(payload.paidAmount),
    coupon_amount: maybeDecimalToNumber(payload.couponAmount),
    amended_amount: maybeDecimalToNumber(payload.amendedAmount),
    job_specs: payload.jobSpecsList,
    provider_cut_amount: maybeDecimalToNumber(payload.providerCutAmount),
    date_created: timestampToISO(payload.dateCreated),
    date_modified: timestampToISO(payload.dateModified),
    date_closed: timestampToISO(payload.dateClosed),
    date_accepted: timestampToISO(payload.dateAccepted),
    date_deadline: timestampToISO(payload.dateDeadline),
    date_transmitted: timestampToISO(payload.dateTransmitted),
    stripe_charges: payload.stripeChargesList,
    requested_pro_id: (_payload$requestedPro = payload.requestedProId) === null || _payload$requestedPro === void 0 ? void 0 : _payload$requestedPro.value,
    requested_pro_public_id: (_payload$requestedPro2 = payload.requestedProPublicId) === null || _payload$requestedPro2 === void 0 ? void 0 : _payload$requestedPro2.value,
    archived: payload.archived,
    waiting_on: mapGrpcJobWaitingOn(payload.waitingOn),
    job_scope_items: payload.jobScopeItemsList.map(function (i) {
      return {
        id: i.id,
        name: i.name,
        description: i.description,
        price: i.price ? decimalToNumber(i.price) : undefined,
        pro_price: i.proPrice ? decimalToNumber(i.proPrice) : undefined
      };
    }),
    consultation: payload.consultation ? mapGrpcJobConsultation(payload.consultation) : undefined,
    type: payload.type
  };
}

/**
 * Maps a Job Proto to a Job DTO for admin.
 *
 * @param {Job} payload - The gRPC Job.
 * @returns {JobDTO} The job DTO.
 */
export function mapGrpcJobToJobDTOForAdmin(payload) {
  var job = mapGrpcJobToJobDTO(payload);
  return _objectSpread(_objectSpread({}, job), {}, {
    admin_fee_amount: maybeDecimalToNumber(payload.adminFeeAmount),
    primary_amount: maybeDecimalToNumber(payload.primaryAmount),
    date_reopened: timestampToISO(payload.dateReopened),
    last_stale_date: timestampToISO(payload.dateLastStale),
    stale: payload.stale,
    payout_total: maybeDecimalToNumber(payload.payoutTotal),
    stripe_payouts: payload.stripePayoutsList
  });
}
function mapGrpcJobPaymentInfoStatus(info) {
  switch (info) {
    case Job.PaymentInfoStatus.NOT_PROVIDED:
      return 'NOT_PROVIDED';
    case Job.PaymentInfoStatus.PROVIDED:
      return 'PROVIDED';
    case Job.PaymentInfoStatus.REQUESTED:
      return 'REQUESTED';
    default:
      throw new BadRequest('Job payment info status unknown');
  }
}
function mapGrpcJobMemberType(type) {
  switch (type) {
    case Job.MemberType.CLIENT:
      return 'CLIENT';
    case Job.MemberType.PROVIDER:
      return 'PROVIDER';
    default:
      throw new BadRequest('Job member type unknown');
  }
}
function mapGrpcJobBillingType(type) {
  switch (type) {
    case Job.BillingType.EXTERNAL:
      return 'EXTERNAL';
    case Job.BillingType.STRIPE:
      return 'STRIPE';
    default:
      throw new BadRequest('Job billing type unknown');
  }
}
function mapGrpcJobMemberRole(role) {
  switch (role) {
    case Job.MemberRole.CHAMPION:
      return 'CHAMPION';
    case Job.MemberRole.SUPPORTING:
      return 'SUPPORTING';
    default:
      throw new BadRequest('Job member role unknown');
  }
}
export function mapGrpcJobStatus(status) {
  switch (status) {
    case Status.CLAIMED:
      return 'CLAIMED';
    case Status.CLOSED:
      return 'CLOSED';
    case Status.IDLE:
      return 'IDLE';
    case Status.ON_HOLD:
      return 'ON_HOLD';
    case Status.UNCLAIMED:
      return 'UNCLAIMED';
    case Status.INFO_GATHERING:
      return 'INFO_GATHERING';
    case Status.UNDER_CONSTRUCTION:
      return 'UNDER_CONSTRUCTION';
    default:
      throw new BadRequest('Job is of an undefined status');
  }
}
function mapGrpcJobResolution(resolution) {
  switch (resolution) {
    case Resolution.RESOLUTION_UNSPECIFIED:
      return null;
    case Resolution.DONE:
      return 'DONE';
    case Resolution.CANCELLED:
      return 'CANCELLED';
    case Resolution.TEST:
      return 'TEST';
    default:
      throw new BadRequest('Job resolution unknown');
  }
}
function mapGrpcJobWaitingOn(waiting) {
  switch (waiting) {
    case WaitingOn.WAITING_ON_CLIENT:
      return 'Client';
    case WaitingOn.WAITING_ON_PRO:
      return 'Provider';
    case WaitingOn.WAITING_ON_UNSPECIFIED:
      return null;
    default:
      throw new BadRequest('Job waiting unknown');
  }
}
function mapGrpcJobTaskDesignee(designee) {
  switch (designee) {
    case Job.Milestone.MilestoneTask.TaskDesignee.CLIENT:
      return 'Client';
    case Job.Milestone.MilestoneTask.TaskDesignee.PROVIDER:
      return 'Provider';
    default:
      throw new BadRequest('Job task designee unknown');
  }
}
function mapGrpcJobAmendmentVisibility(visibility) {
  switch (visibility) {
    case AmendmentVisibility.INTERNAL:
      return 'INTERNAL';
    case AmendmentVisibility.PUBLIC:
      return 'PUBLIC';
    default:
      throw new BadRequest('Job amendment visibility unknown');
  }
}
function mapGrpcJobConsultation(consultation) {
  return {
    id: consultation.id,
    job_id: consultation.jobId,
    status: mapGrpcConsultationStatus(consultation.status),
    availability: consultation.availability ? mapGrpcConsultationAvailability(consultation.availability) : undefined,
    client_user_public_id: consultation.clientUserPublicId,
    provider_user_public_id: consultation.providerUserPublicId ? consultation.providerUserPublicId : undefined,
    created_by_user_public_id: consultation.createdByUserPublicId,
    booked_time: consultation.bookedTime ? {
      start_time: timestampToISO(consultation.bookedTime.startTime),
      end_time: timestampToISO(consultation.bookedTime.endTime)
    } : undefined,
    booking_create_time: consultation.bookingCreateTime ? timestampToISO(consultation.bookingCreateTime) : undefined,
    create_time: timestampToISO(consultation.createTime),
    requested_length: durationToMinutes(consultation.requestedLength),
    skip_scheduling: consultation.skipScheduling,
    room_id: consultation.roomId
  };
}
function mapGrpcConsultationAvailability(availability) {
  var times = availability.availabilityTimesList.map(function (t) {
    return {
      start_time: timestampToISO(t.startTime),
      end_time: timestampToISO(t.endTime)
    };
  });
  return {
    create_time: timestampToISO(availability.createTime),
    availability_times: times
  };
}
function mapGrpcConsultationStatus(status) {
  switch (status) {
    case Consultation.ConsultationStatus.DONE:
      return 'DONE';
    case Consultation.ConsultationStatus.CANCELLED:
      return 'CANCELLED';
    case Consultation.ConsultationStatus.IN_PROGRESS:
      return 'IN_PROGRESS';
    case Consultation.ConsultationStatus.PENDING_CALL:
      return 'PENDING_CALL';
    case Consultation.ConsultationStatus.PENDING_AVAILABILITY:
      return 'PENDING_AVAILABILITY';
    case Consultation.ConsultationStatus.PENDING_BOOKING:
      return 'PENDING_BOOKING';
    default:
      return 'UNKNOWN';
  }
}
function mapCustomerRatingMethod(method) {
  switch (method) {
    case Job.Legend.RatingConfig.CustomerRatingMethod.STARS:
      return 'stars';
    case Job.Legend.RatingConfig.CustomerRatingMethod.THUMBS:
      return 'thumbs';
    default:
      return 'thumbs';
  }
}
function mapGrpcJobLegend(legend) {
  var _legend$priceUnit;
  return {
    id: legend.id,
    name: legend.name,
    version: legend.version,
    allowProSelection: legend.allowProSelection,
    allowScheduling: legend.allowScheduling,
    excludeLegacyYears: legend.excludeLegacyYears,
    excludeLegacyStates: legend.excludeLegacyStates,
    priceUnit: (_legend$priceUnit = legend.priceUnit) === null || _legend$priceUnit === void 0 ? void 0 : _legend$priceUnit.value,
    skipBilling: legend.skipBilling,
    skipQuote: legend.skipQuote,
    deadline_config: legend.deadlineConfig ? {
      enabled: legend.deadlineConfig.enabled,
      expr: legend.deadlineConfig.expr
    } : undefined,
    billing_config: legend.billingConfig ? {
      allow_manual_payment_collection: legend.billingConfig.allowManualPaymentCollection,
      defer_payment_info_collection: legend.billingConfig.deferPaymentInfoCollection
    } : undefined,
    ratingConfig: legend.ratingConfig ? {
      proRatingEnabled: legend.ratingConfig.proRatingEnabled,
      platformRatingEnabled: legend.ratingConfig.platformRatingEnabled,
      customerRatingMethod: mapCustomerRatingMethod(legend.ratingConfig.customerRatingMethod)
    } : undefined
  };
}
//# sourceMappingURL=jobGrpcDtoMapper.js.map
